import { msg, plural, t } from '@lingui/core/macro';
import { z } from 'zod';

import { action, can, defineScreen } from '@/shared/screen/define';
import type { ActionPreview, ScreenContext } from '@/shared/screen/types';

import { createTestEmployeeInput } from './create-test-employee.input';

// Manifesto do "Criar CPF para Teste" (U23/U42). Arquivo leve. A tela só existe com
// `ENABLE_DATAPREV_TEST === 1` (`router.tsx:1317`) e herda `CreditNote:Read` do grupo Crédito. O envio
// escreve na base de homologação da Dataprev: é `write`, com prévia (U24).

export const CREATE_TEST_EMPLOYEE_PATH = '/ccb/criar-cpf-teste';

export const DATAPREV_TEST_FLAG = { key: 'ENABLE_DATAPREV_TEST', equals: 1 } as const;

/** Chamada fora da tela montada: a lista é estado da tela. */
const screenOnly = () => Promise.reject(new Error('dataprev-employee: ação da tela montada'));

export const createTestHistory = action({
  id: 'dataprevEmployee.createTestEmployee.history',
  kind: 'navigate',
  label: msg`Histórico de CPFs Criados`,
  description: msg`Abre a lista dos últimos 10 CPFs de teste criados nesta sessão do navegador (guardada só nesta aba).`,
  run: ({ nav }) => nav.search({ overlay: 'historico', detalhe: undefined }),
});

export const createTestFillFake = action({
  id: 'dataprevEmployee.createTestEmployee.fillFake',
  kind: 'draft',
  label: msg`Preencher com Dados Fictícios`,
  description: msg`Preenche o formulário do trabalhador com dados fictícios sorteados (CPF e CNPJ com dígitos válidos). Nada é enviado.`,
  run: screenOnly,
});

export const createTestAdd = action({
  id: 'dataprevEmployee.createTestEmployee.add',
  kind: 'draft',
  label: msg`Adicionar à Lista`,
  description: msg`Confere um trabalhador fictício com as mesmas regras do formulário e o põe na lista da tela. Nada é enviado à Dataprev.`,
  input: createTestEmployeeInput,
  run: screenOnly,
});

export const createTestRemove = action({
  id: 'dataprevEmployee.createTestEmployee.remove',
  kind: 'draft',
  row: true,
  label: msg`Remover da Lista`,
  description: msg`Tira um trabalhador da lista da tela pela posição (começa em 0). Nada é enviado.`,
  input: z.object({ index: z.number().int().min(0).max(999) }),
  run: screenOnly,
});

/** A prévia do envio: quantos e quem, na homologação da Dataprev. */
export function createTestPreview(names: readonly string[]): ActionPreview {
  const count = names.length;
  return {
    title: plural(count, { one: 'Criar # CPF para teste?', other: 'Criar # CPFs para teste?' }),
    detail: count > 0 ? t`Os trabalhadores ${names.join(', ')} serão incluídos na massa de dados de homologação da Dataprev.` : t`A lista está vazia: adicione pelo menos um CPF antes de enviar.`,
  };
}

export const createTestSubmit = action({
  id: 'dataprevEmployee.createTestEmployee.submit',
  kind: 'write',
  label: msg`Criar CPFs para Teste`,
  description: msg`Envia todos os trabalhadores da lista da tela para a massa de dados de teste da Dataprev, em homologação, e mostra o resultado de cada CPF. Só no ambiente de teste. Pede a confirmação da pessoa.`,
  preview: () => createTestPreview([]),
  run: screenOnly,
});

export const createTestEmployeeScreen = defineScreen({
  id: 'dataprevEmployee.createTestEmployee',
  path: CREATE_TEST_EMPLOYEE_PATH,
  title: msg`Criar CPF para Teste`,
  description: msg`Monta trabalhadores fictícios (vínculo, trabalhador, resumo financeiro e estabelecimento) numa lista da tela e envia todos de uma vez para a massa de dados de teste da Dataprev, em homologação.`,
  permission: can('CreditNote', 'Read'),
  access: { flag: DATAPREV_TEST_FLAG },
  place: { icon: 'user', section: 'credit-note' },
  actions: [createTestHistory, createTestFillFake, createTestAdd, createTestRemove, createTestSubmit],
});

/** O que o agente lê (U24, U46): quantos na lista e o último resultado, no idioma da tela; o CPF sai mascarado. */
export function createTestContext(input: { readonly listed: number; readonly result: string | undefined }): ScreenContext {
  const listed = input.listed;
  const count = plural(listed, { one: '# trabalhador na lista.', other: '# trabalhadores na lista.' });
  const result = input.result;
  return {
    summary: [t`Criar CPF para Teste na Dataprev.`, count, result ? t`Último resultado: ${result}` : ''].filter(Boolean).join(' '),
    fields: { listed: input.listed },
    sensitivity: 'restricted',
  };
}
